export default function PlantationDestinationsSection() {
  const stats = [
    {
      value: "58%",
      label: "of Plantation requests are Caribbean cruise or resort departures with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for business trips through FLL from Plantation professionals",
    },
    {
      value: "Mar–Apr",
      label: "peak spring break season — passport renewals spike just before cruise and resort departures",
    },
    {
      value: "36-hour",
      label: "shortest cruise departure window we've accommodated for a Plantation family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Plantation travelers — whether by cruise from Port Everglades or by direct flight from FLL to Nassau, San Juan, Montego Bay, or Punta Cana. Families, couples, and multi-generational groups from Plantation regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Plantation families seeking beach vacations within a short flight. FLL offers direct and connecting service to Cancun and Cozumel. Business travelers also fly to Mexico City and Guadalajara for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto and Montreal draw Plantation business travelers for finance, healthcare, and tech conferences. Canadian ski destinations like Whistler and Banff attract winter sports enthusiasts from Broward County. Canada requires a valid passport for all air arrivals, and many Plantation families travel with minors whose five-year passports expire faster than expected.",
      urgency: "Business conference registration deadlines often require confirmed travel 4–6 weeks in advance",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom",
      context: "London is a frequent destination for Plantation professionals attending conferences, business meetings, and cultural events. Nova Southeastern University faculty and graduate students also travel to the UK for research collaborations and academic conferences. The UK requires passports valid for the duration of your stay.",
      urgency: "Conference registration and hotel deposits are typically non-refundable with short cancellation windows",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & South America",
      context: "Bogota, Medellin, Cartagena, and Cali draw Plantation travelers with Colombian heritage and business professionals with Latin American clients. FLL offers expanding service to South American destinations through connecting hubs. Each country has its own passport validity requirements, typically six months.",
      urgency: "Family visits to Colombia are often planned with 2–4 weeks of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris for culture and cuisine, Rome and Barcelona for historical tours, Amsterdam for business connections. Plantation families and academic professionals regularly travel to the Schengen Area for vacations, conferences, and educational programs. The Schengen Area requires three months of validity beyond your planned departure.",
      urgency: "Summer tour and study-abroad bookings are made in February–March — passport discoveries peak in April",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Plantation Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Plantation
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Plantation travelers head to the Caribbean, Mexico, Canada, Europe, and South America for a mix of family cruises, beach vacations, business trips, and educational travel. These are the destinations that create the most passport urgency for our Plantation clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Fort Lauderdale", href: "/fort-lauderdale/" },
              { name: "Weston", href: "/weston/" },
              { name: "Aventura", href: "/aventura/" },
              { name: "Boca Raton", href: "/boca-raton/" },
              { name: "Miami", href: "/miami/" },
              { name: "Naples", href: "/naples/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}